import React, { useRef, useEffect } from "react";

/**
 * Performance monitoring wrapper with render tracking
 * Implements the selective memoization strategy from creative phase
 */
export const createMemoizedComponent = <T extends object>(
  Component: React.ComponentType<T>,
  compareProps?: (prev: T, next: T) => boolean
) => {
  const MemoComponent = React.memo(Component, compareProps);

  // Performance monitoring wrapper with render tracking
  return (props: T) => {
    const renderCount = useRef(0);
    const lastRenderTime = useRef(0);

    // Performance monitoring logic
    useEffect(() => {
      renderCount.current++;
      const now = performance.now();

      if (renderCount.current > 1) {
        const timeSinceLastRender = now - lastRenderTime.current;

        // Warn if renders are happening too frequently (< 16ms = above 60fps)
        if (timeSinceLastRender < 16 && renderCount.current > 5) {
          console.warn(
            `Excessive re-renders detected: ${
              Component.displayName || Component.name
            }`
          );
        }
      }

      lastRenderTime.current = now;
    });

    return React.createElement(MemoComponent, props);
  };
};

/**
 * Bundle optimization utility for creating optimized exports
 * Implements tree-shaking optimization from creative phase
 */
export const createOptimizedExports = (components: string[]) => {
  return components.reduce((exports, component) => {
    exports[component] = () => import(`./${component}`);
    return exports;
  }, {} as Record<string, () => Promise<any>>);
};

/**
 * Performance measurement hook for component rendering
 */
export const useRenderPerformance = (componentName: string) => {
  const renderStartTime = useRef(performance.now());
  const renderCount = useRef(0);

  // useEffect(() => {
  //   const renderEndTime = performance.now();
  //   const renderDuration = renderEndTime - renderStartTime.current;
  //   renderCount.current++;

  //   // Log performance if render takes longer than target (16ms for 60fps)
  //   if (renderDuration > 16) {
  //     console.warn(
  //       `Slow render detected in ${componentName}: ${renderDuration.toFixed(
  //         2
  //       )}ms (render #${renderCount.current})`
  //     );
  //   }

  //   // Reset for next render
  //   renderStartTime.current = performance.now();
  // });

  return {
    renderCount: renderCount.current,
  };
};

/**
 * Bundle size monitoring (development only)
 */
export const logBundleInfo = (componentName: string) => {
  if (process.env.NODE_ENV === "development") {
    console.info(`Component loaded: ${componentName}`);
  }
};
